import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { DiplomadoDTO, DiplomadoScheduleDTO } from "../../models/EnrollmentHistoryDTO";
import { horarioBonito } from "../../utils/horarioBonito";
import { precioHorario } from "../../utils/precioHorario";
import { formatLocalDateTime } from "./format";
import { sinAcentos } from "../../utils/cursoCorto";

/** Kind of block that runs in parallel to the others (Inglés in Hotelería): included, no charge of its own. */
const EN_PARALELO = 3;

export interface DiplomadoPick {
	scheduleId: number;
	courseId: number;
	inglesScheduleId: number | null;
	diplomadoName: string;
	blockName: string;
	scheduleName: string;
	inglesScheduleName: string | null;
}

export interface DiplomadoPickerProps {
	campusId: number;
	disabled?: boolean;
	/** Horarios ya elegidos en la solicitud (no se ofrecen dos veces). */
	picked: number[];
	onAdd: (pick: DiplomadoPick) => void;
	/** Lo que se escribe en el buscador de horarios: «hotel» o «belleza» abre aquí su diplomado. */
	buscar?: string;
	/** Ids de los cursos que son diplomados (sus módulos no se venden sueltos). */
	onCargar?: (courseIds: number[]) => void;
}

const http = HttpManager.getInstance();
/** Same rule as the server (CourseBlockService::chocan): both running at the same time and a session on the same day overlaps. */
const chocan = (a: DiplomadoScheduleDTO | undefined, b: DiplomadoScheduleDTO | undefined): boolean => !!a && !!b
	&& a.startDate <= b.endDate && b.startDate <= a.endDate
	&& a.slots.some((x) => b.slots.some((y) => x.day === y.day && x.start < y.end && y.start < x.end));
/** Cards side by side on a wide screen, one column on the phone. */
const rejilla: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 6, marginTop: 4 };

const Horario: React.FC<{ s: DiplomadoScheduleDTO; elegido: boolean; disabled?: boolean; onClick: () => void; aviso?: string; simbolo?: string }> = ({ s, elegido, disabled, onClick, aviso, simbolo }) => (
	<button type="button" disabled={disabled} onClick={onClick} className={"waHorarioTarjeta" + (elegido ? " elegido" : "")} aria-pressed={elegido}>
		<span style={{ display: "flex", gap: 10, alignItems: "center" }}>
			<input type="radio" readOnly tabIndex={-1} checked={elegido} style={{ pointerEvents: "none" }} />
			<b>{horarioBonito(s.name, { plural: true, separador: " " })}</b>
		</span>
		<span style={{ display: "block", fontSize: 13, color: "var(--wa-apagado)", paddingLeft: 26 }}>
			{s.classesGiven > 0 ? `Va en la clase ${s.classesGiven}` : `Inicia ${formatLocalDateTime(s.startDate)}`}
			{s.mentorName ? ` · ${s.mentorName}` : ""}
		</span>
		{s.monthlyPrice ? <span style={{ display: "block", fontSize: 14, fontWeight: 600, paddingLeft: 26 }}>{precioHorario({ coursePlan: { hasPlan: true, monthlyPrice: s.monthlyPrice, billableMonths: s.billableMonths ?? null }, currencySymbol: simbolo })}</span> : null}
		<span style={{ display: "block", fontSize: 13, paddingLeft: 26, color: s.limit > 0 && s.enrolled >= s.limit ? "var(--wa-oro)" : "var(--wa-apagado)" }}>
			Alumnos {s.enrolled}/{s.limit}
		</span>
		{aviso ? <span style={{ display: "block", fontSize: 13, paddingLeft: 26, color: "var(--wa-oro)" }}>{aviso}</span> : null}
	</button>
);

/**
 * Diplomados por bloques (Hotelería, Belleza General; Esteban, 2026-10-25): se elige el diplomado, el horario con el que
 * arranca (de cualquiera de sus bloques, solo horarios que empiezan) y, si lleva Inglés en paralelo, el de Inglés.
 * Queda una sola matrícula del diplomado; Inglés va incluido sin cobro propio.
 */
const DiplomadoPicker: React.FC<DiplomadoPickerProps> = ({ campusId, disabled, picked, onAdd, buscar, onCargar }) => {
	const [diplomados, setDiplomados] = useState<DiplomadoDTO[] | null>(null);
	const [elegido, setElegido] = useState<number | null>(null);
	const [arranque, setArranque] = useState<number | null>(null);
	const [ingles, setIngles] = useState<number | null>(null);

	useEffect(() => {
		let vigente = true;
		http.enrollmentRequestsService.diplomados(campusId)
			.then((r) => { if (vigente) { setDiplomados(r.data || []); if (onCargar) onCargar((r.data || []).map((d) => d.id)); } })
			.catch(() => vigente && setDiplomados([]));
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId]);

	// Buscar «hotel» / «belleza» en los horarios abre el diplomado aquí (no aparece en la lista de horarios sueltos).
	const coincide = (diplomados || []).find((d) => {
		const terminos = sinAcentos(buscar || "").split(/\s+/).filter((t) => t.length >= 3);
		return terminos.length > 0 && terminos.every((t) => sinAcentos(d.name).includes(t));
	}) || null;
	useEffect(() => {
		if (coincide && elegido !== coincide.id) { setElegido(coincide.id); setArranque(null); setIngles(null); }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coincide?.id]);

	if (!diplomados || diplomados.length < 1) {
		return null;
	}
	const d = diplomados.find((x) => x.id === elegido) || null;
	const bloques = d ? d.blocks.filter((b) => b.kindId !== EN_PARALELO) : [];
	const bloqueIngles = d ? d.blocks.find((b) => b.kindId === EN_PARALELO) || null : null;
	const elegir = (id: number | null) => { setElegido(id); setArranque(null); setIngles(null); };
	const horarioArranque = bloques.flatMap((b) => b.schedules).find((s) => s.id === arranque);
	const horarioIngles = bloqueIngles?.schedules.find((s) => s.id === ingles);
	const listo = !!d && arranque !== null && (!bloqueIngles || ingles !== null) && !chocan(horarioArranque, horarioIngles);

	const agregar = () => {
		if (!d || arranque === null) return;
		const bloque = bloques.find((b) => b.schedules.some((s) => s.id === arranque));
		const horario = bloque?.schedules.find((s) => s.id === arranque);
		const horarioIngles = bloqueIngles?.schedules.find((s) => s.id === ingles) || null;
		onAdd({
			scheduleId: arranque, courseId: d.id, inglesScheduleId: horarioIngles ? horarioIngles.id : null, diplomadoName: d.name,
			blockName: bloque?.name || "", scheduleName: horario?.name || "", inglesScheduleName: horarioIngles ? horarioIngles.name : null
		});
		elegir(null);
	};

	return (
		<div className={"waTarjeta" + (coincide ? " waPasoFalta" : "")} style={{ marginBottom: 14 }}>
			<h6 style={{ margin: "0 0 8px" }}>Diplomado por Bloques</h6>
			{coincide ? <div style={{ fontSize: 14, marginBottom: 8 }}>{coincide.name} se vende aquí: elige con qué horario arranca.</div> : null}
			<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
				{diplomados.map((x) => (
					<button key={"dip_" + x.id} type="button" disabled={disabled} className={"waHorarioTarjeta" + (elegido === x.id ? " elegido" : "")} aria-pressed={elegido === x.id}
						style={{ width: "auto", fontWeight: 700 }} onClick={() => elegir(elegido === x.id ? null : x.id)}>
						{x.name.replace(/^Diplomado (de|en) /i, "")}
					</button>
				))}
			</div>
			{d ? (
				<div style={{ marginTop: 12 }}>
					<div className="waPasoSub">¿Con qué horario arranca?</div>
					<div style={{ fontSize: 14, color: "var(--wa-apagado)", marginBottom: 6 }}>
						Cualquiera de los {bloques.length} bloques, en cualquier orden. Solo salen horarios que empiezan.
					</div>
					{bloques.map((b) => (
						<div key={"blq_" + b.id} style={{ marginBottom: 10 }}>
							<b style={{ fontSize: 15 }}>{b.name}</b>
							{b.schedules.filter((s) => !picked.includes(s.id)).length < 1
								? <div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>Sin horarios que empiecen</div>
								: <div style={rejilla}>
									{b.schedules.filter((s) => !picked.includes(s.id)).map((s) => (
										<Horario key={"arr_" + s.id} s={s} simbolo={d.currencySymbol} elegido={arranque === s.id} disabled={disabled}
											onClick={() => { setArranque(s.id); if (chocan(s, horarioIngles)) setIngles(null); }} />
									))}
								</div>}
						</div>
					))}
					{bloqueIngles ? (
						<div style={{ marginTop: 6 }}>
							<div className="waPasoSub">Horario de {bloqueIngles.name}</div>
							<div style={{ fontSize: 14, color: "var(--wa-apagado)", marginBottom: 6 }}>Incluido sin costo los {Math.round(bloqueIngles.sessions / 4)} meses del diplomado.</div>
							{bloqueIngles.schedules.length < 1
								? <div style={{ fontSize: 14, color: "var(--wa-oro)" }}>No hay horarios de {bloqueIngles.name} disponibles</div>
								: <div style={{ ...rejilla, maxHeight: 300, overflow: "auto" }}>
									{bloqueIngles.schedules.map((s) => (
										<Horario key={"ing_" + s.id} s={s} elegido={ingles === s.id} disabled={disabled || chocan(s, horarioArranque)}
											aviso={chocan(s, horarioArranque) ? "Choca con el horario de arranque" : undefined} onClick={() => setIngles(s.id)} />
									))}
								</div>}
						</div>
					) : null}
					<button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 10 }} disabled={disabled || !listo} onClick={agregar}>
						Agregar {d.name.replace(/^Diplomado (de|en) /i, "")}
					</button>
				</div>
			) : null}
		</div>
	);
};

export default DiplomadoPicker;
